import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import type { IntegrationClient } from '../../types';
import { useTranslation } from 'react-i18next';
import { INTEGRATION_CLIENT_ENVIRONMENTS } from './constants';

interface EditIntegrationClientDialogProps {
  client: IntegrationClient | null;
  saving: boolean;
  onClose: () => void;
  onSave: (input: { name: string; environment: string }) => void;
}

export const EditIntegrationClientDialog: React.FC<EditIntegrationClientDialogProps> = ({
  client,
  saving,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState('');

  useEffect(() => {
    setName(client?.name ?? '');
    setEnvironment(client?.environment ?? '');
  }, [client]);

  return (
    <Dialog open={Boolean(client)} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('apiKeys.integrationClients.editTitle')}</DialogTitle>
      <DialogContent>
        <div className="grid gap-3 pt-2">
          <TextField
            fullWidth
            size="small"
            label={t('apiKeys.integrationClients.clientName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            select
            fullWidth
            size="small"
            label={t('apiKeys.modal.environment')}
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
          >
            {INTEGRATION_CLIENT_ENVIRONMENTS.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('apiKeys.integrationClients.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!name.trim() || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => onSave({ name: name.trim(), environment })}
        >
          {t('apiKeys.integrationClients.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface DeleteIntegrationClientDialogProps {
  client: IntegrationClient | null;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeleteIntegrationClientDialog: React.FC<DeleteIntegrationClientDialogProps> = ({
  client,
  deleting,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={Boolean(client)} onClose={deleting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('apiKeys.integrationClients.deleteTitle')}</DialogTitle>
      <DialogContent>
        <p className="text-sm text-gray-700 dark:text-slate-300">
          {t('apiKeys.integrationClients.deleteConfirm', { name: client?.name ?? '' })}
        </p>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          {t('apiKeys.integrationClients.cancel')}
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={onConfirm}
        >
          {t('apiKeys.integrationClients.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
